import React, { Fragment } from "react";
import { Menu, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import SidebarToggleButton from "../layout/SidebarToggleButton";
import PatientModuleLayout from "../patients/layout/PatientModuleLayout";

function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
}

export function ReportShell({
    sidebarOpen,
    setSidebarOpen,
    sidebarOpenDesktop,
    setSidebarOpenDesktop,
    isDesktop,
    loading,
    currentLabel,
    options,
    title,
    description,
    currentScreen = "Reportes",
    actions,
    badges,
    summary,
    children,
}) {
    const reportHeader = (
        <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
            <div className="flex min-h-[72px] items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-6">
                <div className="flex min-w-0 items-center gap-3">
                    <SidebarToggleButton
                        onClick={() => {
                            if (isDesktop) {
                                setSidebarOpenDesktop(!sidebarOpenDesktop);
                                return;
                            }
                            setSidebarOpen(true);
                        }}
                    />
                </div>

                <div className="ml-auto">
                    <Menu as="div" className="relative inline-block text-left">
                        <Menu.Button className="inline-flex items-center justify-center gap-x-1.5 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm">
                            {currentLabel}
                            <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
                        </Menu.Button>
                        <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                        >
                            <Menu.Items className="absolute right-0 z-[60] mt-2 w-56 origin-top-right rounded-2xl border border-slate-200 bg-white p-1 shadow-lg ring-1 ring-black/5 focus:outline-none dark:border-slate-700 dark:bg-slate-900">
                                {options.map((element) => (
                                    <Menu.Item key={element.name}>
                                        {({ active }) => (
                                            <a
                                                href={element.href}
                                                className={classNames(
                                                    active
                                                        ? "bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-white"
                                                        : "text-slate-600 dark:text-slate-300",
                                                    "block rounded-xl px-4 py-2 text-sm",
                                                )}
                                            >
                                                {element.name}
                                            </a>
                                        )}
                                    </Menu.Item>
                                ))}
                            </Menu.Items>
                        </Transition>
                    </Menu>
                </div>
            </div>
        </header>
    );

    return (
        <PatientModuleLayout
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            sidebarOpenDesktop={sidebarOpenDesktop}
            currentScreen={currentScreen}
            header={reportHeader}
        >

                <main className="flex-1 px-4 pb-8 pt-6 sm:px-6 lg:px-8">
                    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950 sm:p-6">
                        <div className="flex flex-col gap-6">
                            <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                                <div className="max-w-3xl">
                                    <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-4xl">
                                        {title}
                                    </h1>
                                    {description ? (
                                        <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300 sm:text-base">
                                            {description}
                                        </p>
                                    ) : null}
                                </div>
                                {actions ? (
                                    <div className="flex flex-wrap gap-3 xl:max-w-[40rem] xl:justify-end">
                                        {actions}
                                    </div>
                                ) : null}
                            </div>

                            {badges ? (
                                <div className="flex flex-wrap gap-3">{badges}</div>
                            ) : null}

                            {summary ? (
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                                    {summary}
                                </div>
                            ) : null}
                        </div>
                    </section>

                    {loading ? (
                        <div className="flex min-h-[420px] items-center justify-center">
                            <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm dark:border-slate-800 dark:bg-slate-950">
                                <div className="mx-auto h-14 w-14 animate-spin rounded-full border-b-2 border-primary" />
                                <h2 className="mt-5 text-xl font-semibold text-slate-900 dark:text-white">
                                    Preparando el reporte
                                </h2>
                                <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                                    Estamos organizando la informacion para que la lectura sea mas clara.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="mt-6 space-y-6">{children}</div>
                    )}
                </main>
        </PatientModuleLayout>
    );
}

export function ReportStatCard({ label, value, tone = "slate", hint }) {
    const tones = {
        slate:
            "border-slate-200 bg-slate-50 text-slate-900 dark:border-slate-800 dark:bg-slate-900 dark:text-white",
        blue: "border-sky-200 bg-sky-50 text-sky-950 dark:border-sky-900/60 dark:bg-sky-950/40 dark:text-sky-100",
        emerald:
            "border-emerald-200 bg-emerald-50 text-emerald-950 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-100",
        rose: "border-rose-200 bg-rose-50 text-rose-950 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-100",
        amber:
            "border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-100",
        violet:
            "border-violet-200 bg-violet-50 text-violet-950 dark:border-violet-900/60 dark:bg-violet-950/40 dark:text-violet-100",
    };

    return (
        <div
            className={classNames(
                "rounded-xl border p-4 shadow-sm",
                tones[tone] || tones.slate,
            )}
        >
            <p className="text-sm font-medium opacity-80">{label}</p>
            <p className="mt-3 text-3xl font-bold tracking-tight">{value}</p>
        </div>
    );
}

export function ReportPanel({ title, description, children, className = "" }) {
    return (
        <section
            className={classNames(
                "rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950 sm:p-6",
                className,
            )}
        >
            {title ? (
                <div className="mb-5">
                    <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
                        {title}
                    </h2>
                </div>
            ) : null}
            {children}
        </section>
    );
}

export function ReportInsightList({ title = "Como leer este reporte", items = [] }) {
    if (!items.length) return null;

    return (
        <ReportPanel title={title} description="Resumen en lenguaje simple para entender rapidamente lo que muestran los datos.">
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
                {items.map((item, index) => (
                    <div
                        key={`${item.title}-${index}`}
                        className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900"
                    >
                        <p className="text-sm font-semibold text-slate-900 dark:text-white">
                            {item.title}
                        </p>
                        <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                            {item.description}
                        </p>
                    </div>
                ))}
            </div>
        </ReportPanel>
    );
}